import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { PawPrint, Sparkles, RotateCcw, Share2, ArrowRight } from "lucide-react";
import { getBreeds } from "@/lib/api";
import { usePageMeta } from "@/lib/usePageMeta";
import { trackGAEvent } from "@/lib/ga";

const SITE_URL = "fetchright.net";

function pick(arr) {
  return arr[Math.floor(Math.random() * arr.length)];
}

// Find breeds at a given trait level, walking outward to the nearest
// non-empty bucket if that exact level has no breeds.
function poolForLevel(buckets, level) {
  if (buckets[level]?.length) return buckets[level];
  for (let d = 1; d <= 4; d++) {
    if (buckets[level - d]?.length) return buckets[level - d];
    if (buckets[level + d]?.length) return buckets[level + d];
  }
  return [];
}

export default function MeterQuiz({ config }) {
  const { BadgeIcon, MeterIcon = PawPrint } = config;
  usePageMeta(config.metaTitle, config.metaDesc);

  const [breeds, setBreeds] = useState([]);
  const [value, setValue] = useState(50);
  const [result, setResult] = useState(null); // { breed, zone }

  useEffect(() => {
    getBreeds({ limit: 200 }).then(setBreeds).catch(() => setBreeds([]));
  }, []);

  const buckets = useMemo(() => {
    const b = { 1: [], 2: [], 3: [], 4: [], 5: [] };
    breeds.forEach((br) => { const v = br[config.traitField]; if (b[v]) b[v].push(br); });
    return b;
  }, [breeds, config.traitField]);

  const zoneFor = (v) => config.zones.find((z) => v < z.max) || config.zones[config.zones.length - 1];
  const liveZone = zoneFor(value);

  const reveal = () => {
    const zone = zoneFor(value);
    const pool = poolForLevel(buckets, zone.level);
    if (!pool.length) { toast.error("Still fetching pups — try again in a sec!"); return; }
    setResult({ breed: pick(pool), zone });
    trackGAEvent(`${config.gaPrefix}_reveal`, { zone: zone.label, level: zone.level });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const spinAgain = () => {
    if (!result) return;
    setResult({ ...result, breed: pick(poolForLevel(buckets, result.zone.level)) });
  };

  const shareCard = async () => {
    const text = result
      ? `I'm "${result.zone.persona}" on the ${config.brandBadge} and my dog match is a ${result.breed.name}! Find yours 👇`
      : `Find your dog match on the ${config.brandBadge} 🐾`;
    const url = `https://${SITE_URL}/${config.slug}`;
    try {
      if (navigator.share) { await navigator.share({ title: config.brandBadge, text, url }); }
      else { await navigator.clipboard.writeText(`${text} ${url}`); toast.success("Link copied — paste it in your TikTok bio!"); }
      trackGAEvent(`${config.gaPrefix}_share`, { method: navigator.share ? "native" : "copy" });
    } catch { /* user dismissed share sheet */ }
  };

  const ShedMeter = ({ level }) => (
    <div className="flex items-center gap-1.5" data-testid="meter-level">
      {[1, 2, 3, 4, 5].map((i) => (
        <MeterIcon
          key={i}
          size={18}
          className={i <= level ? "text-[#E07A5F]" : "text-white/25"}
          fill={i <= level ? "#E07A5F" : "transparent"}
          strokeWidth={2}
        />
      ))}
    </div>
  );

  return (
    <div className="relative min-h-[calc(100vh-4rem)] overflow-hidden">
      <div className="pointer-events-none absolute -top-24 -right-24 w-80 h-80 rounded-full bg-[#F2CC8F]/40 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-32 -left-20 w-96 h-96 rounded-full bg-[#E07A5F]/20 blur-3xl" />

      <div className="relative max-w-5xl mx-auto px-6 lg:px-10 pt-10 pb-24">
        <AnimatePresence>
          {!result ? (
            <motion.div
              key="slider"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
              className="max-w-2xl mx-auto text-center"
            >
              <span className="inline-flex items-center gap-2 rounded-full bg-accent text-accent-foreground px-4 py-1.5 text-xs font-bold uppercase tracking-widest">
                <BadgeIcon size={13} /> {config.brandBadge}
              </span>
              <h1 className="mt-6 font-serif font-black tracking-tight text-accent text-4xl sm:text-5xl lg:text-6xl leading-[0.95]">
                {config.headline[0]}<br />{config.headline[1]}
              </h1>
              <p className="mt-5 text-card-foreground/70 text-base md:text-lg">{config.sub}</p>

              <motion.div
                key={liveZone.label}
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ type: "spring", stiffness: 300, damping: 20 }}
                className="mt-12 mb-2"
              >
                <div className="text-sm font-semibold text-primary uppercase tracking-wider">Currently:</div>
                <div data-testid="meter-live-label" className="font-serif font-black text-accent text-3xl md:text-4xl">
                  {liveZone.label}
                </div>
              </motion.div>

              <div className="mt-8 rounded-[2rem] bg-card border border-border p-8 shadow-[0_8px_30px_rgba(224,122,95,0.12)]">
                <div className="flex justify-between text-xs font-bold uppercase tracking-wider text-muted-foreground mb-4">
                  <span>{config.leftLabel}</span>
                  <span>{config.rightLabel}</span>
                </div>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={value}
                  onChange={(e) => setValue(Number(e.target.value))}
                  data-testid="meter-slider"
                  className="fur-slider w-full"
                  aria-label={config.brandBadge}
                />
              </div>

              <button
                onClick={reveal}
                data-testid="meter-reveal-btn"
                className="pill-btn mt-10 inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-9 py-4 text-lg font-bold shadow-[0_8px_30px_rgba(224,122,95,0.35)] hover:-translate-y-0.5 focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
              >
                <Sparkles size={20} /> {config.ctaLabel}
              </button>
              <div className="mt-4 text-xs text-muted-foreground">Free · no sign-up · takes 3 seconds</div>
            </motion.div>
          ) : (
            <motion.div
              key="card"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.45 }}
              className="max-w-md mx-auto"
            >
              <div className="text-center mb-5">
                <p className="text-sm text-muted-foreground">📸 Screenshot this and post it — tag us!</p>
              </div>

              <motion.div
                initial={{ scale: 0.94 }}
                animate={{ scale: 1 }}
                transition={{ type: "spring", stiffness: 200, damping: 18 }}
                data-testid="meter-result-card"
                className="relative overflow-hidden rounded-[2rem] bg-accent text-accent-foreground shadow-[0_20px_60px_rgba(42,54,35,0.35)]"
              >
                <div
                  className="pointer-events-none absolute inset-0 opacity-[0.06]"
                  style={{ backgroundImage: "radial-gradient(#FDFBF7 1px, transparent 1px)", backgroundSize: "16px 16px" }}
                />
                <div className="pointer-events-none absolute -top-16 -right-10 w-52 h-52 rounded-full bg-[#E07A5F]/30 blur-2xl" />

                <div className="relative p-6 pt-7">
                  <div className="flex items-center justify-between">
                    <span className="inline-flex items-center gap-1.5 font-serif font-black text-lg tracking-tight">
                      <PawPrint size={18} className="text-[#E07A5F]" fill="#E07A5F" /> Fetchright
                    </span>
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1 text-[11px] font-bold uppercase tracking-widest">
                      <BadgeIcon size={12} /> {config.cardBadge}
                    </span>
                  </div>

                  <div className="mt-5 rounded-3xl overflow-hidden aspect-[4/3] ring-1 ring-white/10">
                    <img
                      src={result.breed.image}
                      alt={result.breed.name}
                      className="w-full h-full object-cover"
                      data-testid="meter-result-image"
                    />
                  </div>

                  <div className="mt-6 text-center">
                    <div className="text-[#F2CC8F] text-xs font-bold uppercase tracking-[0.2em]">
                      You are · {result.zone.label}
                    </div>
                    <h2 data-testid="meter-result-persona" className="mt-1.5 font-serif font-black text-3xl leading-tight">
                      {result.zone.persona}
                    </h2>
                  </div>

                  <div className="mt-5 rounded-2xl bg-white/[0.07] border border-white/10 p-4 text-center">
                    <div className="text-xs uppercase tracking-widest text-accent-foreground/60">{config.matchLabel}</div>
                    <div data-testid="meter-result-breed" className="font-serif font-black text-2xl text-white mt-1">
                      {result.breed.name}
                    </div>
                    <div className="mt-3 flex flex-col items-center gap-1.5">
                      <ShedMeter level={result.zone.level} />
                      <span className="text-[11px] text-accent-foreground/60">{config.unitLabel} level {result.zone.level}/5</span>
                    </div>
                  </div>

                  <p className="mt-5 text-center text-[15px] leading-relaxed text-accent-foreground/85">
                    {result.zone.summary}
                  </p>

                  <div className="mt-6 pt-5 border-t border-white/10 text-center">
                    <div className="font-serif font-black text-lg tracking-tight">{SITE_URL}</div>
                    <div className="text-[11px] uppercase tracking-widest text-accent-foreground/50 mt-0.5">
                      Find your perfect dog · free quiz
                    </div>
                  </div>
                </div>
              </motion.div>

              <div className="mt-7 flex flex-wrap items-center justify-center gap-3">
                <button
                  onClick={shareCard}
                  data-testid="meter-share-btn"
                  className="pill-btn inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-6 py-3 text-sm font-bold hover:-translate-y-0.5"
                >
                  <Share2 size={16} /> Share
                </button>
                <button
                  onClick={spinAgain}
                  data-testid="meter-spin-btn"
                  className="pill-btn inline-flex items-center gap-2 rounded-full border-2 border-accent text-accent px-6 py-3 text-sm font-bold hover:bg-accent hover:text-accent-foreground"
                >
                  <RotateCcw size={16} /> Another {result.zone.label} dog
                </button>
                <button
                  onClick={() => setResult(null)}
                  data-testid="meter-restart-btn"
                  className="pill-btn inline-flex items-center gap-2 rounded-full border-2 border-border text-muted-foreground px-6 py-3 text-sm font-bold hover:border-primary/60"
                >
                  Re-slide
                </button>
              </div>

              <div className="mt-8 text-center">
                <Link
                  to="/quiz"
                  data-testid="meter-quiz-cta"
                  className="inline-flex items-center gap-1.5 font-bold text-primary hover:gap-2.5 transition-all"
                >
                  Want the real match? Take the full 15-question quiz <ArrowRight size={16} />
                </Link>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
